import { useState, useMemo, useEffect, useCallback } from "react";
import { isAllBlanksFilled } from "../../../utils/quiz-parser.js";
function parseSelectTextContents(problem) {
  const reg = /\{\{(.*?)}}/gim;
  const contents = [];
  let match;
  while ((match = reg.exec(problem)) !== null) {
    contents.push(match[1]);
  }
  return contents;
}
function parseSelectTextOptions(content) {
  return content.split("|").map((s) => s.trim());
}
function parseSelectTextOptionModels(content) {
  return parseSelectTextOptions(content).map((token, rawIndex) => {
    const isMarker = rawIndex === 0 && token === "fix:";
    const label = token.replace(/`/g, "");
    return {
      rawIndex,
      label,
      isMarker,
      isCorrectMarked: token.includes("`"),
      isSelectable: !isMarker && label.length > 0
    };
  });
}
function useSelectTextQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [startTime] = useState(() => Date.now());
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false);
  const selectData = useMemo(() => {
    return parseSelectTextContents(quiz.problem);
  }, [quiz.problem]);
  const selectCount = selectData.length;
  const [answers, setAnswers] = useState(() => {
    if (initialAnswersProp !== void 0) {
      return new Array(selectCount).fill("").map((_, index) => initialAnswersProp[index] ?? "");
    }
    if (isViewSolved && (solveResult == null ? void 0 : solveResult.correctAnswers)) {
      return solveResult.correctAnswers.map((a) => String(a));
    }
    return new Array(selectCount).fill("");
  });
  const isSolved = solveResult !== null && solveResult !== void 0;
  const canSubmit = isAllBlanksFilled(answers);
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers;
  useEffect(() => {
    onAnswerChange == null ? void 0 : onAnswerChange(answers);
  }, [answers, onAnswerChange]);
  const problemKey = useMemo(
    () => `${quizId}-${selectCount}-${quiz.problem.length}`,
    [quizId, selectCount, quiz.problem]
  );
  const handleToggleDropdown = useCallback((index) => {
    if (isSolved || isViewSolved) return;
    setOpenDropdown((prev) => prev === index ? null : index);
    setCurrentIndex(index);
  }, [isSolved, isViewSolved]);
  const handleSelect = useCallback((selectIndex, optionIndex) => {
    if (isSolved || isViewSolved) return;
    setAnswers((prev) => {
      const newAnswers = [...prev];
      newAnswers[selectIndex] = optionIndex.toString();
      return newAnswers;
    });
    setOpenDropdown(null);
    if (selectIndex < selectCount - 1) {
      setCurrentIndex(selectIndex + 1);
    }
  }, [isSolved, isViewSolved, selectCount]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit || isSolved) return;
    const elapsedTime = Date.now() - startTime;
    onSolve == null ? void 0 : onSolve(quizId, answers, quiz.vars, elapsedTime);
  }, [answers, canSubmit, isSolved, onSolve, quizId, quiz.vars, startTime]);
  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdown !== null) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openDropdown]);
  return {
    answers,
    currentIndex,
    openDropdown,
    selectData,
    selectCount,
    problemKey,
    isSolved,
    canSubmit,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    handleToggleDropdown,
    handleSelect,
    handleSubmit,
    quiz,
    quizId,
    solveResult,
    isViewSolved
  };
}
export {
  parseSelectTextContents,
  parseSelectTextOptionModels,
  parseSelectTextOptions,
  useSelectTextQuiz
};
//# sourceMappingURL=use-select-text-quiz.js.map
